import React, { useContext, useState, useRef, useEffect } from "react";
import styled from "styled-components";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LogarithmicScale,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { ProfileContext } from "../contexts/ProfileContext";
import { LoadingSpinner } from "../components/loading-spinner";
import { useReactToPrint } from "react-to-print";
import OrganizationNameButton from "../components/organization-name-button";
import * as XLSX from "xlsx";

const ChevronDown = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const ChevronRight = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LogarithmicScale,
);

const TypesOfCalls = () => {
  const [dataType, setDataType] = useState("ytd");
  const [exportOption, setExportOption] = useState("");
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempStartDate, setTempStartDate] = useState("");
  const [tempEndDate, setTempEndDate] = useState("");
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [breakdownData, setBreakdownData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isBreakdownOpen, setIsBreakdownOpen] = useState(false);

  const selectRef = useRef(null);
  const datePickerRef = useRef(null);
  const chartRef = useRef();

  // Get Firebase ID from context instead of organizationNumber
  const {
    getCallTypeReport,
    organizationFirebaseId, // Get Firebase ID
    organization,
    worker,
  } = useContext(ProfileContext);

  const formatDate = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate(),
    ).padStart(2, "0")}`;

  // Get Year-to-Date (YTD) range - from Jan 1st to current date
  const getYTDRange = () => {
    const now = new Date();
    const firstDayOfYear = new Date(now.getFullYear(), 0, 1);
    return { start: formatDate(firstDayOfYear), end: formatDate(now) };
  };

  const getDefaultMonthRange = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return { start: formatDate(firstDay), end: formatDate(now) };
  };

  const [selectedRange, setSelectedRange] = useState({ start: "", end: "" });

  const fetchReport = async (type = "", startDate = "", endDate = "") => {
    try {
      setIsLoading(true);

      // Check if we have Firebase ID
      if (!organizationFirebaseId) {
        console.error("❌ No Firebase ID available for organization");
        setIsLoading(false);
        return;
      }

      let result;

      // Only pass dates if a filter is selected
      if (type === "all") {
        const finalStart = "2015-01-01";
        const finalEnd = formatDate(new Date());
        result = await getCallTypeReport(
          organizationFirebaseId, // Pass Firebase ID
          "full",
          finalStart,
          finalEnd,
        );
        setSelectedRange({ start: finalStart, end: finalEnd });
      } else if (type === "ytd") {
        const ytd = getYTDRange();
        result = await getCallTypeReport(
          organizationFirebaseId, // Pass Firebase ID
          "full",
          ytd.start,
          ytd.end,
        );
        setSelectedRange(ytd);
      } else if (type === "monthly") {
        const def = getDefaultMonthRange();
        result = await getCallTypeReport(
          organizationFirebaseId, // Pass Firebase ID
          "full",
          def.start,
          def.end,
        );
        setSelectedRange(def);
      } else if (type === "custom") {
        if (!startDate || !endDate) {
          console.warn("Custom date range selected but no dates provided");
          return;
        }
        result = await getCallTypeReport(
          organizationFirebaseId, // Pass Firebase ID
          "full",
          startDate,
          endDate,
        );
        setSelectedRange({ start: startDate, end: endDate });
      } else {
        // No filter selected - fetch without dates
        result = await getCallTypeReport(organizationFirebaseId, "full"); // Pass Firebase ID
        setSelectedRange({ start: "", end: "" });
      }

      console.log(result);

      // Extract data from the new API response structure
      const apiData = result || {};

      const parentData = {};
      const children = {};

      // Process the API response
      Object.entries(apiData).forEach(([category, data]) => {
        const count = data.count || 0;
        const subcategories = data.subcategories || {};

        // Add to parent data (main categories)
        parentData[category] = count;

        // Add to breakdown data if subcategories exist
        if (Object.keys(subcategories).length > 0) {
          children[category] = subcategories;
        }
      });

      const parentLabels = Object.keys(parentData);
      const parentValues = Object.values(parentData);

      const colors = [
        "#FF7234",
        "#FCD5C9",
        "#2DB3C0",
        "#C1DCDE",
        "#EC3310",
        "#DE735E",
        "#048097",
        "#FFB0A2",
        "#BFE9EE",
        "#00323B",
        "#E8B1A7",
        "#EC3310",
      ];

      const barColors = parentValues.map((_, i) => colors[i % colors.length]);

      setChartData({
        labels: parentLabels,
        datasets: [
          {
            label: "Total Calls",
            data: parentValues,
            backgroundColor: barColors,
            borderRadius: 6,
          },
        ],
      });

      // Set breakdown data for categories with subcategories
      setBreakdownData(children);
    } catch (error) {
      console.error("❌ Error fetching call type report:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Fetch initial data with YTD filter by default only if Firebase ID exists
    if (organizationFirebaseId) {
      fetchReport("ytd");
    }
  }, [organizationFirebaseId]); // Add organizationFirebaseId as dependency

  const handleApply = () => {
    if (!tempStartDate || !tempEndDate) return alert("Select both dates.");
    setDataType("custom");
    setShowDatePicker(false);
    fetchReport("custom", tempStartDate, tempEndDate);
  };

  const handleCancel = () => {
    // Reset to empty dates when canceling custom date picker
    setTempStartDate("");
    setTempEndDate("");
    setShowDatePicker(false);
    // Don't change dataType here, let user choose another filter
  };

  useEffect(() => {
    const selectElement = selectRef.current;
    if (!selectElement) return;
    const handleMouseDown = () => setIsSelectOpen((prev) => !prev);
    selectElement.addEventListener("mousedown", handleMouseDown);
    const handleBlur = () => setIsSelectOpen(false);
    selectElement.addEventListener("blur", handleBlur);
    return () => {
      selectElement.removeEventListener("mousedown", handleMouseDown);
      selectElement.removeEventListener("blur", handleBlur);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
    };
    if (showDatePicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDatePicker]);

  const chartOptions = {
    responsive: true,
    scales: {
      y: {
        type: "linear",
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          callback: (value) => (Number.isInteger(value) ? value : null),
        },
      },
    },
    plugins: {
      title: { display: false },
      legend: { display: false },
    },
    maintainAspectRatio: false,
  };

  // 🧾 PDF Export
  const handlePrint = useReactToPrint({
    content: () => chartRef.current,
    documentTitle: `Types_of_Calls_Report_${selectedRange.start || "all"}_to_${
      selectedRange.end || "all"
    }`,
    onBeforePrint: () => {
      document.body.style.width = "100%";
    },
    onAfterPrint: () => {
      setExportOption("");
      document.body.style.width = "";
    },
  });

  // 📊 Excel Export
  const handleExportExcel = () => {
    if (!chartData || !chartData.labels.length) {
      alert("No data available to export.");
      return;
    }

    const worksheetData = chartData.labels.map((label, i) => ({
      "Type of Call": label,
      "Total Calls": chartData.datasets[0].data[i],
    }));

    // ✅ Add breakdown data if exists
    Object.entries(breakdownData).forEach(([parent, children]) => {
      worksheetData.push({});
      worksheetData.push({ "Type of Call": `${parent} Breakdown` });
      Object.entries(children).forEach(([sub, value]) => {
        worksheetData.push({
          "Type of Call": `  - ${sub}`,
          "Total Calls": value,
        });
      });
    });

    const ws = XLSX.utils.json_to_sheet(worksheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Call Report");

    const fileName = `Types_of_Calls_Report_${
      selectedRange.start || "all"
    }_to_${selectedRange.end || "all"}.xlsx`;
    XLSX.writeFile(wb, fileName);

    setExportOption("");
  };

  // 🧠 Watch for exportOption changes
  useEffect(() => {
    if (exportOption === "print") handlePrint();
    if (exportOption === "export") handleExportExcel();
  }, [exportOption]);

  // Toggle breakdown card visibility
  const toggleBreakdown = () => {
    setIsBreakdownOpen(!isBreakdownOpen);
  };

  return (
    <Wrapper>
      <TopSection>
        {(worker && worker.role === "subscriber") ||
        organization?.name?.toLowerCase() === "crossroads" ? null : (
          <OrganizationNameButton />
        )}

        <TitleText>Types of Calls</TitleText>
      </TopSection>

      <ToggleButtonContainer>
        <ToggleContent>
          <ToggleButtonGroup>
            <ToggleButton
              active={dataType === "ytd"}
              onClick={() => {
                setDataType("ytd");
                fetchReport("ytd");
              }}
            >
              YEAR TO DATE
            </ToggleButton>

            <ToggleButton
              active={dataType === "all"}
              onClick={() => {
                setDataType("all");
                fetchReport("all");
              }}
            >
              ALL DATA
            </ToggleButton>

            <ToggleButton
              active={dataType === "monthly"}
              onClick={() => {
                setDataType("monthly");
                const def = getDefaultMonthRange();
                fetchReport("monthly", def.start, def.end);
              }}
            >
              CURRENT MONTH
            </ToggleButton>

            <ToggleButton
              active={dataType === "custom"}
              onClick={() => {
                setDataType("custom");
                setShowDatePicker(true);
              }}
            >
              CUSTOM DATE RANGE
            </ToggleButton>
          </ToggleButtonGroup>

          <ExportContainer>
            <SelectWrapper>
              <ExportSelect
                ref={selectRef}
                value={exportOption}
                onChange={(e) => setExportOption(e.target.value)}
              >
                <option value="" disabled>
                  Export Options
                </option>
                <option value="print">Print PDF</option>
                <option value="export">Export as Excel Sheet</option>
              </ExportSelect>
              <ArrowIcon isOpen={isSelectOpen}>
                <ChevronDown />
              </ArrowIcon>
            </SelectWrapper>
          </ExportContainer>
        </ToggleContent>

        {showDatePicker && (
          <DatePickerDropdown ref={datePickerRef}>
            <DateInputGroup>
              <label>From</label>
              <input
                type="date"
                value={tempStartDate}
                onChange={(e) => setTempStartDate(e.target.value)}
              />
            </DateInputGroup>

            <DateInputGroup>
              <label>To</label>
              <input
                type="date"
                value={tempEndDate}
                onChange={(e) => setTempEndDate(e.target.value)}
              />
            </DateInputGroup>

            <DatePickerActions>
              <button onClick={handleApply}>Apply</button>
              <button onClick={handleCancel}>Cancel</button>
            </DatePickerActions>
          </DatePickerDropdown>
        )}
      </ToggleButtonContainer>

      <printContainer ref={chartRef}>
        {/* ========== HIDDEN PDF-ONLY TITLE ========== */}
        <PdfOnlyHeader>
          <div className="main-title">TYPES OF CALLS</div>
        </PdfOnlyHeader>
        <ChartContainer>
          <ChartBox>
            {isLoading ? (
              <SpinnerWrapper>
                <LoadingSpinner />
              </SpinnerWrapper>
            ) : (
              <Bar data={chartData} options={chartOptions} />
            )}
          </ChartBox>
        </ChartContainer>

        {Object.keys(breakdownData).length > 0 && (
          <BreakdownCard isOpen={isBreakdownOpen}>
            <BreakdownTitle onClick={toggleBreakdown}>
              <ChevronIcon>
                {isBreakdownOpen ? <ChevronDown /> : <ChevronRight />}
              </ChevronIcon>
              Call Breakdown
            </BreakdownTitle>

            {isBreakdownOpen && (
              <BreakdownContent>
                <BreakdownGrid>
                  {Object.entries(breakdownData)
                    .sort(([a], [b]) => {
                      // Sort categories with subcategories in a specific order
                      const order = [
                        "Financial Needs",
                        "Healing",
                        "Family Concerns",
                        "Emotional Needs",
                      ];
                      const aIndex = order.indexOf(a);
                      const bIndex = order.indexOf(b);
                      if (aIndex === -1 && bIndex === -1)
                        return a.localeCompare(b);
                      if (aIndex === -1) return 1;
                      if (bIndex === -1) return -1;
                      return aIndex - bIndex;
                    })
                    .map(([parent, subcategories]) => {
                      const childLabels = Object.keys(subcategories);
                      const childValues = Object.values(subcategories);

                      const colors = [
                        "#FF7234",
                        "#FCD5C9",
                        "#2DB3C0",
                        "#C1DCDE",
                        "#EC3310",
                        "#DE735E",
                        "#048097",
                        "#FFB0A2",
                        "#BFE9EE",
                        "#00323B",
                        "#E8B1A7",
                        "#EC3310",
                      ];
                      const barColors = childValues.map(
                        (_, idx) => colors[idx % colors.length],
                      );

                      return (
                        <SubChartBox key={parent}>
                          <h4>{parent} Breakdown</h4>
                          {isLoading ? (
                            <SpinnerWrapper>
                              <LoadingSpinner />
                            </SpinnerWrapper>
                          ) : (
                            <Bar
                              data={{
                                labels: childLabels,
                                datasets: [
                                  {
                                    label: parent,
                                    data: childValues,
                                    backgroundColor: barColors,
                                    borderRadius: 6,
                                    barThickness: "flex",
                                    maxBarThickness: 60,
                                  },
                                ],
                              }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                layout: {
                                  padding: { top: 10, bottom: 10 },
                                },
                                plugins: {
                                  legend: { display: false },
                                  title: { display: false },
                                },
                                scales: {
                                  y: {
                                    beginAtZero: true,
                                    ticks: {
                                      stepSize: 1,
                                      callback: (value) =>
                                        Number.isInteger(value) ? value : null,
                                      font: { size: 11 },
                                    },
                                    grid: {
                                      color: "rgba(0,0,0,0.05)",
                                    },
                                  },
                                  x: {
                                    ticks: {
                                      font: { size: 11 },
                                      autoSkip: false,
                                      maxRotation: 50,
                                      minRotation: 20,
                                    },
                                    grid: { display: false },
                                  },
                                },
                              }}
                            />
                          )}
                        </SubChartBox>
                      );
                    })}
                </BreakdownGrid>
              </BreakdownContent>
            )}
          </BreakdownCard>
        )}
      </printContainer>
    </Wrapper>
  );
};

export default TypesOfCalls;

// ---------------- Styled Components ----------------

const PdfOnlyHeader = styled.div`
  display: none;

  @media print {
    display: block;
    text-align: center;
    margin: 40px 0;
    padding-bottom: 20px;
    font-family: "Helvetica", "Arial", sans-serif;

    .main-title {
      text-align: center;
      font-family: Recoleta, serif;
      font-size: 1.25rem;
      font-weight: bold;
      color: #ec3310;
      margin-bottom: 0;
      text-decoration: underline;
      grid-column: 2;

      @media (max-width: 768px) {
        grid-column: 1;
        font-size: 1.1rem;
      }

      @media (max-width: 480px) {
        font-size: 1rem;
      }
    }
  }
`;

const Wrapper = styled.div`
  width: 100%;
  padding: 2rem;
  background-color: #f3f9fa;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 1rem 0;
  }
`;

const SpinnerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const TopSection = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  margin-bottom: 1rem;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    justify-items: center;
    gap: 0.75rem;
  }
`;

const ToggleButtonContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: start;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    justify-content: center;
    padding: 0 1rem;
  }
`;

const ToggleContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const ToggleButtonGroup = styled.div`
  display: flex;
  background: #eeeeee;
  border: 1px solid #e5e7eb;
  border-radius: 62px;
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
    border-radius: 12px;
    max-width: 300px;
    width: 100%;
  }

  @media (max-width: 480px) {
    max-width: 100%;
  }
`;

const ExportContainer = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    justify-content: center;
    width: 100%;
  }
`;

const ToggleButton = styled.button`
  width: auto;
  min-width: 164px;
  height: 29px;
  padding: 0 12px;

  font-family: "Futura PT", sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0.15em;
  text-align: center;

  border: none;
  border-radius: 62px;
  cursor: pointer;
  transition: all 0.3s ease;

  background: ${({ active }) => (active ? "#2DB3C0" : "transparent")};
  color: ${({ active }) => (active ? "white" : "#86CCDA")};
  box-shadow: ${({ active }) =>
    active ? "0 2px 6px rgba(0, 0, 0, 0.15)" : "none"};

  white-space: nowrap;

  @media (max-width: 768px) {
    min-width: 100%;
    width: 100%;
    height: 40px;

    font-size: 14px; /* Keep typography consistent */
    letter-spacing: 0.15em; /* Keep consistent */
    border-radius: 8px;
    margin: 2px 0;
  }

  @media (max-width: 480px) {
    height: 36px;

    font-size: 14px; /* Maintain required size */
    letter-spacing: 0.25em; /* Maintain required spacing */

    padding: 0 8px;
  }
`;

const DatePickerDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  min-width: 280px;
  animation: slideDown 0.2s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    left: 50%;
    transform: translateX(-50%);
  }

  @media (max-width: 480px) {
    min-width: 260px;
    padding: 12px;
  }
`;

const DateInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;

  label {
    font-size: 13px;
    color: #333;
    font-weight: 500;
    font-family: "Space Grotesk";
  }

  input {
    padding: 8px 10px;
    border-radius: 6px;
    border: 1px solid #d1d5db;
    font-size: 13px;
    font-family: "Space Grotesk";
    transition: all 0.2s;

    &:focus {
      outline: none;
      border-color: #2db3c0;
      box-shadow: 0 0 0 2px rgba(45, 179, 192, 0.1);
    }
  }
`;

const DatePickerActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;

  button {
    flex: 1;
    padding: 8px 12px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;
    font-family: "Space Grotesk";
    font-weight: 600;
    border: none;
    transition: all 0.2s;

    &:first-child {
      background-color: #2db3c0;
      color: white;

      &:hover {
        background-color: #249aa5;
      }
    }

    &:last-child {
      background-color: #f3f4f6;
      color: #666;

      &:hover {
        background-color: #e5e7eb;
      }
    }
  }
`;

const SelectWrapper = styled.div`
  position: relative;
  width: 172px;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 200px;
  }
`;

const ExportSelect = styled.select`
  appearance: none;
  background: #faf8f8;
  border: 1px solid #106271;
  color: #106271;
  font-family: "Space Grotesk";
  border-radius: 8px;
  width: 100%;
  height: 29px;
  font-size: 14px;
  cursor: pointer;
  transition: 0.2s;
  padding: 0 10px;

  &:focus {
    outline: none;
  }
`;

const ArrowIcon = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%)
    rotate(${({ isOpen }) => (isOpen ? "180deg" : "0deg")});
  color: #6b7280;
  pointer-events: none;
  transition: transform 0.3s ease;

  svg {
    height: 20px;
    width: 20px;
  }
`;

const TitleText = styled.h2`
  text-align: center;
  font-family: Recoleta, serif;
  font-size: 1.25rem;
  font-weight: bold;
  color: #ec3310;
  margin-bottom: 0;
  text-decoration: underline;
  grid-column: 2;

  @media (max-width: 768px) {
    grid-column: 1;
    font-size: 1.1rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const ChartContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const ChartBox = styled.div`
  width: 100%;
  padding: 24px;
  background: white;
  border-radius: 16px;
  border: 1px solid #f3f4f6;
  height: 400px;
`;

const BreakdownCard = styled.div`
  width: 100%;
  margin: 2.5rem auto 0 auto;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #f3f4f6;
  padding: ${({ isOpen }) => (isOpen ? "24px" : "16px 24px")};
  transition: all 0.3s ease;
`;

const BreakdownTitle = styled.p`
  font-family: "Space Grotesk", sans-serif;
  font-size: 14px;
  font-weight: 700; /* Bold */
  font-style: normal;
  line-height: 100%;
  color: #2db3c0;
  letter-spacing: 0.25em; /* 25% spacing */
  text-align: center;
  text-transform: uppercase;

  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #249aa5;
  }

  @media (max-width: 480px) {
    font-size: 14px; /* Keeping typography consistent */
    letter-spacing: 0.25em; /* Keeping consistent */
  }
`;

const ChevronIcon = styled.span`
  display: flex;
  align-items: center;
  transition: transform 0.3s ease;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const BreakdownContent = styled.div`
  margin-top: 1rem;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const BreakdownGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(550px, 1fr));
  gap: 24px;
  width: 100%;
`;

const SubChartBox = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #f3f4f6;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  height: auto;
  min-height: 320px;

  h4 {
    font-family: "Space Grotesk";
    font-size: 14px;
    font-weight: 700;
    color: #ec3310;
    margin-bottom: 8px;
    text-align: center;
  }

  canvas {
    width: 100% !important;
    height: 280px !important;
  }

  @media (max-width: 900px) {
    canvas {
      height: 240px !important;
    }
  }

  @media (max-width: 600px) {
    padding: 12px;
    canvas {
      height: 220px !important;
    }

    h4 {
      font-size: 13px;
    }
  }
`;
